import React, { memo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import styles from './MiniProductCard.module.scss';

export interface MiniProductCardProps {
  displayName: string;
  displayPrice: string;
  displaySalePrice?: string;
  imageURL: string;
  href: string;
  title?: string;
  withBorder?: boolean;
  priority?: boolean;
  trackClick?: () => void;
}

const MiniProductCard = ({
  displayName,
  displayPrice,
  displaySalePrice,
  imageURL,
  href,
  title,
  withBorder = false,
  priority = false,
  trackClick,
}: MiniProductCardProps) => {
  const { t } = useTranslation('common');

  const displaySale = !!displaySalePrice;

  const parsedDisplayPrice = parseFloat(
    displayPrice?.replace(/[^0-9.]/g, '') || '0'
  );
  const parsedSalePrice = parseFloat(
    displaySalePrice?.replace(/[^0-9.]/g, '') || '0'
  );
  const discountPercent =
    displaySale && parsedDisplayPrice > 0
      ? Math.round((1 - parsedSalePrice / parsedDisplayPrice) * 100)
      : 0;

  const ariaLabel = `${displayName}, ${displaySalePrice || displayPrice}`;

  return (
    <article className={styles.card} data-border={withBorder}>
      <a
        className={styles.link}
        href={href}
        aria-label={ariaLabel}
        onClick={() => trackClick?.()}
      >
        <div className={styles.imageWrapper}>
          <div className={styles.imagePad}>
            <Image
              src={imageURL}
              alt=""
              fill
              sizes="(min-width: 988px) 104px, 80px"
              className={styles.image}
              priority={priority}
              loading={priority ? undefined : 'eager'}
            />
          </div>
        </div>
        <div className={styles.info}>
          {title && <p className={styles.title}>{title}</p>}
          <p className={styles.name}>{displayName}</p>
          <div className={styles.priceRow}>
            {displaySalePrice ? (
              <>
                <span className={styles.salePrice}>{displaySalePrice}</span>
                <s className={styles.originalPrice}>{displayPrice}</s>
                <span className={styles.badge}>
                  {t('productStrip.discountBadge', {
                    percent: discountPercent,
                  })}
                </span>
              </>
            ) : (
              <span className={styles.price}>{displayPrice}</span>
            )}
          </div>
        </div>
      </a>
    </article>
  );
};

export default memo(MiniProductCard);
